import { Collapse, Row, Col, Skeleton, Select, Tag, DatePicker } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { Utils } from "../../common/misc/Utils";
import TaskViewVisualStore from "../stores/TaskViewVisualStore";
import ActorBadge from "./ActorBadge";
import { Dayjs } from "dayjs";
import { TaskCommentsStore } from "../stores";
import { useNavigate } from "react-router";

type Props = {
    store: TaskViewVisualStore;
    commentStore: TaskCommentsStore;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getExpandIcon: (panelProps: any) => JSX.Element;
    disabledDate: (current: Dayjs) => boolean;
};
const DetailsSection: React.FC<Props> = ({
    store,
    getExpandIcon,
    disabledDate,
}) => {
    const navigate = useNavigate();

    const taskPreview = store.taskPreview!;
    const reminderTimestamp = Utils.getDateTimestamp(
        taskPreview.reminder,
        true,
    );
    const dueDateTimestamp = Utils.getDateTimestamp(taskPreview.dueDate, true);
    const shouldShowTaskTypeField = !store.taskType?.isSystemDefault;

    const handleRelatedTaskClick = () => {
        if (store.getRelatedTask()) {
            navigate(`/tasks/${store.getRelatedTask()}`);
        }
    };

    const collapseItems = [
        {
            key: "details",
            label: "Details",
            children: (
                <div className="dialog-section">
                    <Row className="inputs-container input-row">
                        <Col span={10}>
                            <span className="input-label">Owner</span>
                        </Col>
                        <Col span={14} className="value-container">
                            <ActorBadge
                                store={store}
                                style={{ marginLeft: "-4px" }}
                                className="sender-avatar"
                                actor={taskPreview.createdByActor}
                                legacyUserName={
                                    store.allUsersFullNameResolver[
                                        taskPreview.createdBy
                                    ]
                                }
                                legacyUserId={taskPreview.createdBy}
                                size={24}
                            />
                        </Col>
                    </Row>
                    {shouldShowTaskTypeField && (
                        <Row className="inputs-container input-row">
                            <Col span={10}>
                                <span className="input-label">Task type</span>
                            </Col>
                            <Col span={14} className="value-container">
                                {store.taskTypesLoading ? (
                                    <div
                                        style={{ marginTop: 8, height: "16px" }}
                                    >
                                        <Skeleton
                                            title={false}
                                            avatar={false}
                                            paragraph={{ rows: 1 }}
                                            active
                                        />
                                    </div>
                                ) : (
                                    <Select
                                        className={`borderless-select ${!taskPreview.taskType ? "required" : ""}`}
                                        placeholder={
                                            taskPreview.taskType &&
                                            store.taskTypesLoading
                                                ? "Loading task type..."
                                                : "To be defined"
                                        }
                                        loading={store.taskTypesLoading}
                                        disabled={store.taskTypeChangeDisabled}
                                        style={{ marginLeft: "-12px" }}
                                        value={store.taskType?.id}
                                        onChange={(option: unknown) =>
                                            store.updateField(
                                                store.taskPreview!.id,
                                                "TaskType",
                                                option,
                                                () =>
                                                    store.handleTypeChange(
                                                        option as string,
                                                    ),
                                            )
                                        }
                                    >
                                        {store.taskTypes &&
                                            store.taskPreviewTypes.map((t) => (
                                                <Select.Option
                                                    key={t.id}
                                                    value={t.id}
                                                    style={{
                                                        display: t.isEnabled
                                                            ? "block"
                                                            : "none",
                                                    }}
                                                >
                                                    <>
                                                        {t.name}
                                                        {t.isSystemDefault ? (
                                                            <Tag
                                                                className="alpha-info-tag"
                                                                style={{
                                                                    marginLeft: 8,
                                                                }}
                                                            >
                                                                System managed
                                                            </Tag>
                                                        ) : null}
                                                    </>
                                                </Select.Option>
                                            ))}
                                    </Select>
                                )}
                            </Col>
                        </Row>
                    )}
                    <Row className="inputs-container input-row">
                        <Col span={10}>
                            <span className="input-label">Priority</span>
                        </Col>
                        <Col span={14} className="value-container">
                            <Select
                                disabled={store.taskFormLocked}
                                style={{ marginLeft: "-12px" }}
                                className={`priority-select ${taskPreview.priority}`}
                                value={taskPreview.priority}
                                onChange={(option: unknown) =>
                                    store.updateField(
                                        taskPreview.id,
                                        "Priority",
                                        option,
                                    )
                                }
                            >
                                <Select.Option value="LOW">
                                    <Tag
                                        color="#9BA0AA"
                                        className="alpha-info-tag"
                                    >
                                        Low
                                    </Tag>
                                </Select.Option>
                                <Select.Option value="MEDIUM">
                                    <Tag
                                        color="#FADB14"
                                        className="alpha-info-tag"
                                    >
                                        Medium
                                    </Tag>
                                </Select.Option>
                                <Select.Option value="HIGH">
                                    <Tag
                                        color="#F5222D"
                                        className="alpha-info-tag"
                                    >
                                        High
                                    </Tag>
                                </Select.Option>
                            </Select>
                        </Col>
                    </Row>

                    <Row className="inputs-container input-row">
                        <Col span={10}>
                            <span className="input-label">Reminder</span>
                        </Col>
                        <Col span={14} className="value-container">
                            <DatePicker
                                disabled={
                                    store.taskFormLocked || !store.hasDueDate
                                }
                                className="borderless-picker"
                                suffixIcon={
                                    <i className="alpha-icon xs calendar-icon" />
                                }
                                placeholder="Set reminder"
                                disabledDate={disabledDate}
                                showNow={false}
                                value={Utils.formatDatePickerValue(
                                    taskPreview.reminder,
                                    true,
                                    true,
                                )}
                                onChange={(m) =>
                                    store.updateField(
                                        taskPreview.id,
                                        "Reminder",
                                        Utils.formatDateAsUtcMidnightIso(m),
                                        undefined,
                                        true,
                                    )
                                }
                                format={Utils.getDateFormat()}
                            />
                            {taskPreview.dueDate &&
                                reminderTimestamp !== null &&
                                dueDateTimestamp !== null &&
                                reminderTimestamp > dueDateTimestamp && (
                                    <div className="task-date-warning">
                                        Reminder is after due date
                                    </div>
                                )}
                        </Col>
                    </Row>
                    <Row className="inputs-container input-row">
                        <Col span={10}>
                            <span className="input-label">Related task</span>
                        </Col>
                        <Col
                            span={14}
                            style={{ cursor: "pointer" }}
                            className="value-container"
                        >
                            <span onClick={handleRelatedTaskClick}>
                                {(store.getRelatedTask() && "Link") || "-"}
                            </span>
                        </Col>
                    </Row>
                </div>
            ),
        },
    ];

    return (
        <div data-id-type="collapse-container" data-id-name="Details">
            <Collapse
                expandIcon={getExpandIcon}
                bordered={false}
                defaultActiveKey="details"
                className="alpha-task-section-collapse"
                items={collapseItems}
            />
        </div>
    );
};
export default observer(DetailsSection);
